import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Observable, map } from 'rxjs';
import type { KeywordInterface } from '../models/keyword.interface';
import type { GetStudiesReponse } from './studies.service';
import { GetStudyDetailsQuery, StudiesQuery, StudiesQueryVariables } from './studies.service';

@Injectable({
  providedIn: 'root'
})
export class KeywordService {
  GetKeywordsQuery = gql`query keywords($clinicId: Long!) {
    keywords(clinicId: $clinicId, take: 10000) {
      items {
        id
        label
        disciplinedName
        disciplinedId
      }
    }
  }`;

  GetKeywordsQueryVariables: { clinicId?: number } = { };

  constructor(private readonly apollo: Apollo) { }

  getKeywords(clinicId: number): Observable<KeywordInterface[]> {
    this.GetKeywordsQueryVariables = { clinicId };
    return this.apollo.query<{ keywords: { items: KeywordInterface[] } }>({
      query: this.GetKeywordsQuery,
      variables: this.GetKeywordsQueryVariables
    }).pipe(map(res => res.data.keywords?.items ?? []));
  }

  searchKeywords(clinicId: number, search: string): Observable<KeywordInterface[]> {
    return this.apollo.query<{ searchKeywords: { object: KeywordInterface; distance: number }[] }>({
      query: gql`query searchKeywords($clinicId: Long!, $search: String!) {
        searchKeywords(clinicId: $clinicId, search: $search, take: 5) {
          object {
            id
            label
            disciplinedId
          }
          distance
        }
      }`,
      variables: { clinicId, search }
    }).pipe(map(res => res.data.searchKeywords.map(wrapper => {
      const keyword = { ...wrapper.object };
      keyword.distance = wrapper.distance;
      return keyword;
    }).filter(keyword => (keyword.distance ?? 1) >= 0.7)) );
  }

  addOrUpdateKeyword(label: string, disciplinedId: number, id?: number): Observable<KeywordInterface | undefined> {
    return this.apollo.mutate<{ addOrUpdateKeyword: KeywordInterface }>({
      mutation: gql`mutation addOrUpdateKeyword($dto: KeywordAddOrUpdateDtoInput) {
        addOrUpdateKeyword(dto: $dto) {
          id
          disciplinedName
          label
          disciplinedId
        }
      }`,
      variables: {
        dto: {
          label,
          disciplinedId,
          id: id ?? null,
        },
      },
      update: (store, { data }) => {

        const keywordsQuery = store.readQuery<GetKeywordsQueryResponse>({ query: this.GetKeywordsQuery, variables : this.GetKeywordsQueryVariables });
        const keywords = keywordsQuery?.keywords?.items?.map(keywords=> ({ ...keywords }));
        if (data && keywords) {
          if (keywords.map(k=> k.id).includes(data?.addOrUpdateKeyword.id)) {
            const keyword = keywords.find(k=> k.id === data?.addOrUpdateKeyword.id);
            if (keyword) {
              keyword.disciplinedId = data?.addOrUpdateKeyword.disciplinedId;
              keyword.label = data?.addOrUpdateKeyword.label;
              keyword.disciplinedName = data?.addOrUpdateKeyword.disciplinedName;
            }
          } else {
            keywords.push(data?.addOrUpdateKeyword);
          }
        }

        keywords?.sort((a, b) => a.label.localeCompare(b.label));
        store.writeQuery({ query: this.GetKeywordsQuery, variables : this.GetKeywordsQueryVariables, data: { keywords: { items: keywords } } });
      },
    }).pipe(map(res => res.data?.addOrUpdateKeyword));
  }

  addOrUpdateKeywordToStudy(keywords: number[], studyId: number ): Observable<KeywordInterface[] | undefined> {
    return this.apollo.mutate<{ addKeywordToStudy: KeywordInterface[];
    }>({
      mutation: gql`mutation addKeywordToStudy($dto: KeywordAddToStudyInput) {
        addKeywordToStudy(dto: $dto) {
          id
          label
          clinicId
          clinicName
        }
      }`,
      variables: {
        dto: {
          keywordIds : keywords,
          studyId
        }
      },
      refetchQueries: [{
        query : GetStudyDetailsQuery,
        variables: { studyId }
      }],
      update: (store, { data }) => {
        // Read the data from our cache for this query.
        const studiesQuery = store.readQuery<GetStudiesReponse>({ query: StudiesQuery, variables: StudiesQueryVariables });
        if (studiesQuery && data) {
          const listStudyItems = studiesQuery.studies.items.map(s=> ({ ...s }));
          const study = listStudyItems.find(s=>s.id === studyId);

          if (study) {
            study.keywords = data.addKeywordToStudy;
          }

          const newStudies = {
            studies: {
              items: listStudyItems,
              pageInfo: studiesQuery.studies.pageInfo,
              totalCount: studiesQuery.studies.totalCount
            }
          };
          store.writeQuery({ query: StudiesQuery, variables : StudiesQueryVariables, data : newStudies });
        }

        const studyDetailsQuery = store.readQuery({ query: GetStudyDetailsQuery, variables: { studyId } }) as any;

        if (studyDetailsQuery && data) {
          const study = { ...studyDetailsQuery.study };

          if (study) {
            study.keywords = data.addKeywordToStudy;
          }

          store.writeQuery({ query: GetStudyDetailsQuery, variables: { studyId }, data : { study }  });
        }
      },
    }).pipe(map(res => res.data?.addKeywordToStudy));
  }

  deleteKeyword(id: number): Observable<KeywordInterface | undefined> {
    return this.apollo.mutate<{ deleteKeyword: KeywordInterface }>({
      mutation: gql`mutation deleteKeyword($id: Long!) {
        deleteKeyword(id : $id) {
          id
          label
          disciplinedName
        }
      }`,
      variables: { id }
    }).pipe(map(res => res.data?.deleteKeyword));
  }
}

export interface GetKeywordsQueryResponse {
  keywords: { items: KeywordInterface[] };
}
